import fetch from '@/assets/js/src/util/fetch'
import { checkResponse, handleException, paramsToQuery, } from '@/assets/js/src/util/apiTools'
import { safeDecodeURI, } from '@/assets/js/src/util/safeDecodeURI'
import cookieManager from '@/assets/js/src/util/cookieManagerWrapper'
import { safeEncodeURI, } from '@/assets/js/src/util/safeEncodeURI'
import { getDefaultHeaders, } from '@/assets/js/src/util/fetch/defaultHeaders'
import { useAutocompleteStore, } from '@/assets/js/src/modules/autocomplete/_pinia/autocomplete'
import { defineStore, getActivePinia, } from 'pinia'
import { useRouteMetaStore, } from '@/assets/js/src/pinia/routeMeta'
import { useAppUiStore, } from '@/assets/js/src/pinia/appUi'
import { useBibleStore, } from '@/assets/js/src/modules/bible/_pinia/bible.js'
import {useUserStore} from "@/assets/js/src/modules/user/_pinia/user.js"

const API_PREFIX_SEARCH = '/api/search'

export const HYBRID = 'hybrid'
export const CLASSIC = 'classic'
export const COHERENCE_VALUES = [ 100, 70, 50, 30, 1, ]

const defaultOptionsClassic = {
    fullwords: true,
    umlaute: false,
    exactPhrase: false,
    searchHeadings: false,
    areaClassic: [],
}

const defaultOptionsSmart = {
    coherence: 2,
    areaSmart:["1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47","48","49","50","51","52","53","54","55","56","57","58","59","60","61","62","63","64","65","66",],
}

const compressArea = (area) => {
    let set = new Set(area.map(String))

    let atNumbers = Array.from(set).filter(n => parseInt(n) >= 1 && parseInt(n) <= 39)
    if (atNumbers.length === 39) {
        atNumbers.forEach(n => set.delete(n))
        set.add('at')
    }

    let ntNumbers = Array.from(set).filter(n => parseInt(n) >= 40 && parseInt(n) <= 66)
    if (ntNumbers.length === 27) {
        ntNumbers.forEach(n => set.delete(n))
        set.add('nt')
    }

    let akNumbers = Array.from(set).filter(n => parseInt(n) >= 90 && parseInt(n) <= 96)
    if (akNumbers.length === 7) {
        akNumbers.forEach(n => set.delete(n))
        set.add('ak')
    }

    return Array.from(set)
}

const expandArea = (area) =>{
    let set = new Set(area.map(String))

    if (set.has('at')) {
        for (let i = 1; i <= 39; i++) set.add(String(i))
        set.delete('at')
    }
    if (set.has('nt')) {
        for (let i = 40; i <= 66; i++) set.add(String(i))
        set.delete('nt')
    }
    if (set.has('ak')) {
        for (let i = 90; i <= 96; i++) set.add(String(i))
        set.delete('ak')
    }

    return Array.from(set)
}

const normalizeFilter = (optionsString) =>{
    try {
        let options = JSON.parse(optionsString)

        if (options.type === HYBRID) {
            let smartOptions = Object.keys(defaultOptionsSmart).reduce((acc, key) => {
                acc[key] = options[key]

                return acc
            }, {})
            smartOptions.areaSmart = compressArea(smartOptions.areaSmart)
            return JSON.stringify({
                type: HYBRID,
                ...smartOptions,
            })
        }

        if (options.type === CLASSIC) {
            let classicOptions = Object.keys(defaultOptionsClassic).reduce((acc, key) => {
                acc[key] = options[key]

                return acc
            }, {})
            classicOptions.areaClassic = compressArea(classicOptions.areaClassic)
            return JSON.stringify({
                type: CLASSIC,
                ...classicOptions,
            })
        }
    } catch (e) {
        return optionsString
    }
    return optionsString
}

export const useSearchStore = defineStore('search', {
    state: () => ({
        searchResult: {},
        ...defaultOptionsClassic,
        ...defaultOptionsSmart,
        page: 1,
        exceeded: false,
        canonical: 0,
        lastCanonical: 0,
        offsets: {},
        active: false,
        type: null,
    }),
    getters: {
        areaForApi (state) {
            let area = state[state.isHybridSearch ? 'areaSmart' : 'areaClassic']
            return compressArea(area)
        },
        maxPages: (state) => state.searchResult.max_pages,
        defaults: (state) => {
            let user = useUserStore(getActivePinia()).user
            let type = user?.options?.searchOptions?.type ?? HYBRID
            return {
                type,
                ...defaultOptionsClassic,
                ...defaultOptionsSmart,
            }
        },
        defaultsHash: (state) => {
            return JSON.stringify(state.defaults)
        },
        options: (state) => {
            return Object
                .keys(state.defaults)
                .reduce((acc, key) => {
                    acc[key] = state[key]

                    return acc
                }, {})
        },
        optionsHash: (state) => {
            return JSON.stringify(state.options)
        },
        optionsAsJsonString: (state) => {
            let filter = state.optionsHash
            filter = normalizeFilter(filter)
            return JSON.stringify({ filter, })
        },
        optionsAsParams (state) {
            return paramsToQuery(JSON.parse(state.optionsAsJsonString || '{}'))
        },
        isHybridSearch: (state) => state.type === HYBRID,
    },
    actions: {
        init () {
            if(null !== this.type) {
                return this
            }

            let user = useUserStore(getActivePinia()).user
            this.type = user?.options?.searchOptions?.type ?? HYBRID
            return this
        },
        appendSearchResult (result) {
            Object.keys(result).forEach((abbreviation) => {
                Object.keys(result[abbreviation].marks).forEach((verseId) => {
                    this.searchResult.result[abbreviation].marks[verseId] = result[abbreviation].marks[verseId]
                })
                this.searchResult.result[abbreviation].verses.push(...result[abbreviation].verses)
                Object.keys(result[abbreviation].booknames).forEach((bookNumber) => {
                    if (typeof this.searchResult.result[abbreviation].booknames[bookNumber] === 'undefined') {
                        this.searchResult.result[abbreviation].booknames[bookNumber] = result[abbreviation].booknames[bookNumber]
                    }
                })
            })
        },
        setOptions (options) {
            for(let option in options) {
                this[option] = options[option]
            }
        },
        updateType (userOptions) {
            this.type = userOptions.searchOptions.type ?? HYBRID
        },
        setOptionsFromParams (params) {
            if (!params.filter) {
                return
            }
            params = JSON.parse(params.filter)
            for(let option in this.defaults) {
                if (typeof params[option] === 'undefined') {
                    continue
                }

                if (['areaClassic', 'areaSmart',].includes(option)) {
                    this[option] = expandArea(params[option])
                    continue
                }

                this[option] = params[option]
            }
        },

        async fetchSearch ({ canonical, page, route, searchTerm, }) {
            try {
                let activePinia = getActivePinia()

                this.active = true
                let params = route.params
                let abbr = ((typeof params.abbreviation !== 'undefined') ? params.abbreviation : '') + ((typeof params.rest !== 'undefined') ? '.' + params.rest : '')

                if (!abbr) {
                    let bibleStore = useBibleStore(activePinia)
                    abbr = encodeURIComponent(bibleStore.selectedBibles.join('.'))
                }

                let search = searchTerm || params.slug || ''
                Array.isArray(search) && (search = search.join())

                if (!search.length) {
                    return
                }

                let routeMetaStore = useRouteMetaStore(activePinia)

                let newRoute = `/search/${safeEncodeURI(abbr)}/${safeEncodeURI(search)}`

                // Parameter aus routeMeta übernehmen
                if (Object.keys(routeMetaStore.params).length !== 0) {
                    this.setOptionsFromParams(routeMetaStore.params)
                    routeMetaStore.deleteParams()
                }

                if (canonical === 0) {
                    this.exceeded = false
                }

                // Exakte Phrase prüfen
                if (this.exactPhrase) {
                    search = `"${search.replace(/^"|"$/gm, '')}"`
                }

                if (!import.meta.env.SSR) {
                    let appUiStore = useAppUiStore(activePinia)
                    if (appUiStore.mobileView && !appUiStore.mobileViewLandscape) {
                        abbr = abbr.split('.').slice(0, 1).join('.')
                    } else if (appUiStore.mobileView && appUiStore.mobileViewLandscape) {
                        abbr = abbr.split('.').slice(0, 2).join('.')
                    }
                }

                let rootState = activePinia.state.value

                let url = `${API_PREFIX_SEARCH}/${this.type === HYBRID && !/^"|"$/gm.test(search) ? HYBRID : CLASSIC}`

                // Api-Request senden
                let apiResponse = await fetch({
                    url,
                    options: {
                        method: 'POST',
                        body: JSON.stringify({
                            search,
                            abbr,
                            coherence: COHERENCE_VALUES[this.coherence],
                            area: this.areaForApi,
                            fullwords: this.fullwords,
                            umlaute: this.umlaute,
                            searchHeadings: this.searchHeadings,
                            offsets: page === 1 ? {} : this.offsets,
                            canonical,
                            page,
                        }),
                        headers: getDefaultHeaders({}),
                    },
                })

                // Prüfen der Api Antwort
                if (checkResponse(apiResponse)) {
                    throw new Error('Fehler in Api-Response')
                }

                this.type = url.split('/')[3] === HYBRID
                    ? HYBRID
                    : CLASSIC

                let appUiStore = useAppUiStore(getActivePinia())

                let countRoutes = [ ...new Set(appUiStore.lastRoutes), ].length
                countRoutes > 1 && this.sendBsaEvent(search)

                // Die search-Options im Cookie setzen
                if (this.optionsAsJsonString) {
                    cookieManager.set('bs-params', this.optionsAsJsonString, {
                        secure: true,
                    })
                } else if (cookieManager.get('bs-params')) {
                    cookieManager.remove('bs-params')
                }

                this.lastCanonical = apiResponse.data?.last_canonical ?? 0
                this.page = apiResponse.data.page

                let count = Object.values(apiResponse.data.result).reduce((carry, value) => carry + parseInt(value.count), 0)

                if (page === 1) {
                    this.searchResult = apiResponse.data
                } else if (count > 0) {
                    this.appendSearchResult(apiResponse.data.result)
                } else {
                    this.exceeded = true
                }

                this.offsets = apiResponse.data.offsets ?? {}
                this.canonical = canonical

                // Such-Options einlenden
                let autocompleteStore = useAutocompleteStore(getActivePinia())
                autocompleteStore.isSearch = true

                // Auch RouteMeta laden
                if (!import.meta.env.SSR && (safeDecodeURI(newRoute) !== rootState.routeMeta.canonical)) {
                    routeMetaStore.loadRouteMeta({
                        url: newRoute.slice(1),
                        type: 'search',
                        reload: false,
                    })
                }

            } catch (e) {
                handleException(e, false, false)
            } finally {
                this.active = false
            }
        },

        sendBsaEvent (searchTerm) {
            let event = {
                eventCategory: 'search',
                eventAction: this.type,
                eventLabel: searchTerm ?? '',
            }

            if(this.$bsa) {
                this.$bsa.event(event)

                return
            }

            const Matomo = window.Matomo?.getAsyncTracker()
            if(Matomo) {
                Matomo.trackEvent(Object.values(event))
            }
        },
    },
})
